// Held-work enqueue signal for the PowerSync capture write path (issue #754).
//
// The held-count indicator (hooks/powersync/use-unsynced-capture-count.ts)
// re-probes on the connector's upload/drop/quarantine events, on
// online/visibility wakes, and — while `navigator.onLine === false` — on a short
// poll. None of those sees a LOCAL WRITE that lands while the device reports
// `navigator.onLine === true` but no packets actually flow (a real dead zone:
// hangar / basement). After a synced checkpoint quiesces the poll, an offline
// +/- tap in that state enqueues to ps_crud yet fires no connector event and
// leaves `isOffline()` false, so the banner shows a stale "all saved". See the
// enqueue-channel header in hooks/powersync/upload-events.ts.
//
// The one signal that the held total rose is the local write itself. Rather than
// scatter an `_emitEnqueued()` call across every dispatch helper (and silently
// miss a NEW write path added later — the exact wrong-signal the indicator
// exists to prevent), this wraps the PowerSync db ONCE at the capture client's
// boundary: every `execute` (and every `writeTransaction` inner `execute`) emits
// the pulse after the write commits. A future dispatch path is covered with no
// remembered edit — the same single-chokepoint discipline the combined
// `subscribeToHeldChange` union uses on the consumer side.
//
// `getAll` is left un-wrapped: the capture client uses `execute` for writes and
// `getAll` for reads (a read never queues ps_crud), so wrapping `execute` alone
// fires on exactly the writes.

import { _emitEnqueued } from '../../hooks/powersync/upload-events'

import type {
  PowerSyncDb,
  PowerSyncWriteTx,
} from './capture-client-powersync-db'

// Writes to PowerSync's RAW underlying tables (`ps_data__*`) update the local
// mirror directly WITHOUT queuing a ps_crud entry — e.g. the
// dispatchSessionStateChange buildings-status mirror write. They don't raise the
// held-work total, so they must not emit an enqueue pulse. Every synced-VIEW
// write (capture_items / capture_sessions / spaces / asset_types) does queue, and
// none of those names contains `ps_data__`, so no real enqueue is suppressed.
// The `\b` matches the raw prefix even when the identifier is quoted (the
// backtick→`p` transition is a word boundary), so a quoted `ps_data__*` write is
// suppressed too.
//
// Module-scope regex (not a per-call literal) so a write burst doesn't allocate
// a fresh RegExp on every execute (audit-N perf).
const RAW_TABLE_WRITE = /\bps_data__/i
function queuesCrud(sql: string): boolean {
  return !RAW_TABLE_WRITE.test(sql)
}

/**
 * Wrap a PowerSync db so every queue-generating write emits the held-work
 * enqueue pulse (`_emitEnqueued`) after it commits. Reads (`getAll`) and raw
 * `ps_data__*` writes do not emit. The pulse is best-effort: `_emitEnqueued`
 * isolates subscriber exceptions internally, so it can never reject the
 * surveyor's capture mutation.
 */
export function withEnqueueSignal(db: PowerSyncDb): PowerSyncDb {
  const wrapTx = (tx: PowerSyncWriteTx): PowerSyncWriteTx => ({
    async execute(sql: string, params?: unknown[]): Promise<unknown> {
      const result = await tx.execute(sql, params)
      if (queuesCrud(sql)) _emitEnqueued()
      return result
    },
  })

  const wrapped: PowerSyncDb = {
    getAll<T = Record<string, unknown>>(
      sql: string,
      params?: unknown[],
    ): Promise<T[]> {
      return db.getAll<T>(sql, params)
    },
    async execute(sql: string, params?: unknown[]): Promise<unknown> {
      const result = await db.execute(sql, params)
      if (queuesCrud(sql)) _emitEnqueued()
      return result
    },
  }

  // Narrow into a const so the closure below doesn't need a non-null assertion
  // (the optional method is preserved only when the underlying db has it).
  //
  // `.bind(db)` is LOAD-BEARING, not stylistic: PowerSync's real
  // `writeTransaction` runs `await this.waitForReady()` as its FIRST statement
  // (@powersync/common AbstractPowerSyncDatabase). A bare `db.writeTransaction`
  // reference invoked later loses its receiver, so `this` is `undefined` and
  // that line throws `Cannot read properties of undefined (reading
  // 'waitForReady')` — which is exactly what every surveyor saw on room delete
  // (the only dispatch path that goes through writeTransaction). `getAll` /
  // `execute` above are called AS methods on `db`, so they kept their binding;
  // only this extracted reference lost it. Plain-object unit mocks don't touch
  // `this`, so the bug was invisible to tests until the real db ran in the
  // field — bind here AND model `this` in the regression test.
  const baseWriteTransaction = db.writeTransaction?.bind(db)
  if (baseWriteTransaction) {
    wrapped.writeTransaction = <T>(
      callback: (tx: PowerSyncWriteTx) => Promise<T>,
      lockTimeoutMs?: number,
    ): Promise<T> =>
      baseWriteTransaction<T>((tx) => callback(wrapTx(tx)), lockTimeoutMs)
  }

  return wrapped
}
